import {
  CTCSS_TONES,
  DTCS_CODES,
  compareImages,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const BLOCK_SIZE = 16
const MEMORY_SIZE = 0x07a0
const CHANNEL_OFFSET = 0x0170
const CHANNEL_SIZE = 16
const CHANNEL_COUNT = 99
const WRITE_START = 0x0150
const WRITE_END = 0x07a0
const MAGIC = Uint8Array.of(0x32, 0x31, 0x05, 0x10, 0x78)
const EXIT = Uint8Array.of(0x32, 0x31, 0x05, 0xee, 0x56)
const NO_TONE = 0xc000

export type RB15Profile = {
  protocol: 'rb15'
  id: string
  vendor: 'Retevis'
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 1
  channelMaximum: 99
  nameLength: 0
  powerLevels: readonly ['High', 'Low']
  memorySize: number
}

const RB15_SPECS = [
  {
    id: 'retevis-rb15',
    model: 'RB15',
    summary:
      'Compact Retevis UHF radio with 99 channels, tones, and split transmit.',
  },
  {
    id: 'retevis-rb615',
    model: 'RB615',
    summary:
      'PMR-oriented sibling of the RB15 with the same 99-channel memory map.',
  },
] as const

export const RB15_PROFILES: readonly RB15Profile[] = RB15_SPECS.map(
  (spec) => ({
    protocol: 'rb15',
    id: spec.id,
    vendor: 'Retevis',
    model: spec.model,
    summary: spec.summary,
    tags: ['Experimental', 'RB15 protocol', 'Read + write', '99 channels'],
    baudRate: 9600,
    channelMinimum: 1,
    channelMaximum: CHANNEL_COUNT,
    nameLength: 0,
    powerLevels: ['High', 'Low'],
    memorySize: MEMORY_SIZE,
  }),
)

function emptyChannel(
  number: number,
  profile: RB15Profile,
): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'NFM',
    power: profile.powerLevels[0],
    skip: false,
  }
}

function readUint32BigEndian(raw: Uint8Array, offset: number): number {
  return (
    ((raw[offset] << 24) |
      (raw[offset + 1] << 16) |
      (raw[offset + 2] << 8) |
      raw[offset + 3]) >>>
    0
  )
}

function writeUint32BigEndian(
  raw: Uint8Array,
  offset: number,
  value: number,
): void {
  raw[offset] = (value >>> 24) & 0xff
  raw[offset + 1] = (value >>> 16) & 0xff
  raw[offset + 2] = (value >>> 8) & 0xff
  raw[offset + 3] = value & 0xff
}

function decodeToneValue(value: number): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  if (value === 0 || value === NO_TONE || value === 0xffff) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  const code = value & 0x3fff
  if ((value & 0x4000) !== 0) {
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: Number.parseInt(code.toString(8).padStart(3, '0'), 10),
      dtcsPolarity: 'N',
    }
  }
  if ((value & 0x8000) !== 0) {
    return {
      toneMode: 'dtcs',
      ctcss: null,
      dtcs: Number.parseInt(code.toString(8).padStart(3, '0'), 10),
      dtcsPolarity: 'R',
    }
  }
  return {
    toneMode: 'ctcss',
    ctcss: code / 10,
    dtcs: null,
    dtcsPolarity: 'N',
  }
}

export function parseRB15Channel(
  image: Uint8Array,
  profile: RB15Profile,
  number: number,
): WebChannel {
  if (number < profile.channelMinimum || number > profile.channelMaximum) {
    throw new Error(`Channel ${number} is outside the ${profile.model} range.`)
  }
  const offset = CHANNEL_OFFSET + (number - profile.channelMinimum) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  const rxRaw = readUint32BigEndian(raw, 0)
  if (
    raw.byteLength !== CHANNEL_SIZE ||
    rxRaw === 0 ||
    rxRaw === 0xffffffff
  ) {
    return emptyChannel(number, profile)
  }
  const txRaw = readUint32BigEndian(raw, 6)
  const rxFrequency = rxRaw * 10
  const txFrequency = txRaw === 0xffffffff ? null : txRaw * 10
  const rxTone = (raw[4] << 8) | raw[5]
  const txTone = (raw[10] << 8) | raw[11]
  const decodedTone =
    rxTone !== NO_TONE && rxTone !== 0xffff
      ? decodeToneValue(rxTone)
      : decodeToneValue(txTone)
  return {
    number,
    empty: false,
    name: '',
    rxFrequency,
    txFrequency,
    ...decodedTone,
    mode: (raw[12] & 0x20) !== 0 ? 'NFM' : 'FM',
    power: (raw[12] & 0x80) !== 0 ? profile.powerLevels[1] : profile.powerLevels[0],
    skip: (raw[12] & 0x04) === 0,
  }
}

export function parseRB15Channels(
  image: Uint8Array,
  profile: RB15Profile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum },
    (_, index) => parseRB15Channel(image, profile, index + profile.channelMinimum),
  )
}

function encodeDcs(code: number, polarity: 'N' | 'R'): number {
  return (
    Number.parseInt(code.toString(), 8) |
    (polarity === 'R' ? 0x8000 : 0x4000)
  )
}

function encodeTone(channel: WebChannel): number {
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    const index = CTCSS_TONES.indexOf(
      channel.ctcss as (typeof CTCSS_TONES)[number],
    )
    if (index < 0) throw new Error('Unsupported CTCSS tone for this Retevis.')
    return Math.round(channel.ctcss * 10)
  }
  if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = DTCS_CODES.indexOf(channel.dtcs as (typeof DTCS_CODES)[number])
    if (index < 0) throw new Error('Unsupported DTCS code for this Retevis.')
    return encodeDcs(channel.dtcs, channel.dtcsPolarity)
  }
  return NO_TONE
}

export function applyRB15Channel(
  image: Uint8Array,
  profile: RB15Profile,
  channel: WebChannel,
): void {
  const offset = CHANNEL_OFFSET + (channel.number - profile.channelMinimum) * CHANNEL_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  const preservedFlags = raw[12] & 0x1a
  const preservedUnknown = raw.slice(13, 16)
  raw.fill(0)
  raw.set(preservedUnknown, 13)
  writeUint32BigEndian(raw, 0, Math.round(channel.rxFrequency / 10))
  if (channel.txFrequency === null) {
    raw.fill(0xff, 6, 10)
  } else {
    writeUint32BigEndian(raw, 6, Math.round(channel.txFrequency / 10))
  }
  const tone = encodeTone(channel)
  raw[4] = (tone >> 8) & 0xff
  raw[5] = tone & 0xff
  raw[10] = (tone >> 8) & 0xff
  raw[11] = tone & 0xff
  raw[12] =
    preservedFlags |
    (channel.power === 'Low' ? 0x80 : 0) |
    (channel.mode === 'NFM' ? 0x20 : 0) |
    (channel.skip ? 0 : 0x04)
}

function blockChecksum(data: Uint8Array): number {
  let checksum = 0
  for (const value of data) checksum = (checksum + value) & 0xff
  return checksum
}

async function enterProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.discardInput()
  for (let attempt = 0; attempt < 5; attempt += 1) {
    await transport.write(MAGIC)
    try {
      if ((await transport.readExact(1, 1000))[0] === ACK) return
    } catch {
      // Retry the RB15 wake sequence.
    }
  }
  throw new Error(
    'The Retevis radio did not enter RB15 programming mode. Check the cable and power-cycle the radio.',
  )
}

async function exitProgrammingMode(transport: RadioTransport): Promise<void> {
  await transport.write(EXIT)
}

async function readBlock(
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  const command = Uint8Array.of(
    0x52,
    (address >> 8) & 0xff,
    address & 0xff,
  )
  await transport.write(Uint8Array.of(...command, blockChecksum(command)))
  const response = await transport.readExact(3 + BLOCK_SIZE + 1, 2500)
  if (
    response[0] !== command[0] ||
    response[1] !== command[1] ||
    response[2] !== command[2]
  ) {
    throw new Error(`Unexpected RB15 read reply at 0x${address.toString(16)}.`)
  }
  if (blockChecksum(response.slice(0, -1)) !== response.at(-1)) {
    throw new Error(`Bad RB15 checksum at 0x${address.toString(16)}.`)
  }
  return response.slice(3, -1)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const command = Uint8Array.of(
    0x57,
    (address >> 8) & 0xff,
    address & 0xff,
  )
  const body = new Uint8Array(3 + data.byteLength)
  body.set(command)
  body.set(data, 3)
  await transport.write(Uint8Array.of(...body, blockChecksum(body)))
  if ((await transport.readExact(1, 2500))[0] !== ACK) {
    throw new Error(`RB15 rejected write block 0x${address.toString(16)}.`)
  }
}

export async function readRB15RadioWithTransport(
  profile: RB15Profile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  await enterProgrammingMode(transport)
  const image = new Uint8Array(profile.memorySize)
  const total = profile.memorySize / BLOCK_SIZE
  for (let address = 0; address < profile.memorySize; address += BLOCK_SIZE) {
    image.set(await readBlock(transport, address), address)
    onProgress?.({
      current: address / BLOCK_SIZE + 1,
      total,
      label: `Reading ${profile.model} memory`,
    })
  }
  await exitProgrammingMode(transport)
  return { image, reportedModel: profile.model }
}

export async function writeRB15RadioWithTransport(
  profile: RB15Profile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error(`The selected image is smaller than the ${profile.model} memory map.`)
  }
  await enterProgrammingMode(transport)
  const total = (WRITE_END - WRITE_START) / BLOCK_SIZE
  let completed = 0
  for (let address = WRITE_START; address < WRITE_END; address += BLOCK_SIZE) {
    await writeBlock(
      transport,
      address,
      image.slice(address, address + BLOCK_SIZE),
    )
    completed += 1
    onProgress?.({
      current: completed,
      total,
      label: `Writing ${profile.model} memory`,
    })
  }
  await exitProgrammingMode(transport)
}
